import { VenueSurchargesType } from '@api';
import { FormActions } from '@common';
import { zodResolver } from '@hookform/resolvers/zod';
import { NumberField, TextareaField } from '@hypeit-gmbh/hypeit-ui';
import { Stack } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  useUpdateVenueSurcharges,
  useVenueSurchargesFormSchema,
  VenueSurchargesFormDataType,
} from './hooks';

const TRANS_PATH = 'VENUE_SURCHARGES.FORM';

type VenueSurchargesFormPropsType = {
  venueId: string;
  overviewVersion: string;
  venueSurcharges: VenueSurchargesType;
  onClose: () => void;
};

export const VenueSurchargesForm: React.FC<VenueSurchargesFormPropsType> = ({
  venueId,
  overviewVersion,
  venueSurcharges,
  onClose,
}) => {
  const { t } = useTranslation('venueDetailsOverview');

  const { schema, defaultValues } =
    useVenueSurchargesFormSchema(venueSurcharges);
  const { updateVenueSurcharges } = useUpdateVenueSurcharges({
    venueId,
    overviewVersion,
    onClose,
  });

  const methods = useForm<VenueSurchargesFormDataType>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const handleSubmit = async (data: VenueSurchargesFormDataType) => {
    await updateVenueSurcharges(data);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(handleSubmit)}>
        <Stack>
          <NumberField<VenueSurchargesFormDataType>
            fieldName="facilityFee"
            label={t(`${TRANS_PATH}.FACILITY_FEE_LABEL`)}
            suffix="€"
          />
          <NumberField<VenueSurchargesFormDataType>
            fieldName="publicTransportFee"
            label={t(`${TRANS_PATH}.PUBLIC_TRANSPORT_FEE_LABEL`)}
            suffix="€"
          />
          <TextareaField<VenueSurchargesFormDataType>
            fieldName="notes"
            label={t(`${TRANS_PATH}.NOTES_LABEL`)}
          />
          <FormActions
            onCancel={onClose}
            saveButtonText={t('SAVE_BUTTON', { ns: 'common' })}
            cancelButtonText={t('CANCEL_BUTTON', { ns: 'common' })}
          />
        </Stack>
      </form>
    </FormProvider>
  );
};
